import { useEffect, useMemo } from 'react';
import { useAppSelector } from '../../../app/hooks';
import { selectLastScanDataUpdateTimestamp } from '../../scanDataApi/scanDataSlice';
import { useDuckDbQuery } from '../third-party-kit/hooks/useDuckDbQuery';

export type DuckQueryReturnListType<T> = {
  isLoading: boolean;
  error: Error | null;
  data: T[] | null;
  query: string | null;
};

export type DuckQueryReturnOneType<T> = {
  isLoading: boolean;
  error: Error | null;
  data: T | null;
  query: string | null;
};

/**
 * useDuckQueryGeneric is a custom React hook that handles the execution of a given DuckDB query.
 *
 * Note: This hook is tightly coupled to the existing ScanData flow
 *
 * @param {string | null} query - The SQL query to be executed.
 * @param {boolean | undefined} skip - Condition to skip query execution.
 *
 * @returns {DuckQueryReturnType<T>} - Returns an array of query results if successful, or null otherwise.
 */
function useDuckQueryScanData<T>(
  query: string | null,
  skip: boolean | undefined = false
): DuckQueryReturnListType<T> {
  const { arrow, loading, error, forceUpdate } = useDuckDbQuery(
    query || undefined,
    skip
  );
  const dbUpdateTrigger = useAppSelector(selectLastScanDataUpdateTimestamp);

  useEffect(() => {
    forceUpdate();
  }, [dbUpdateTrigger, forceUpdate]);

  const result = useMemo(() => {
    if (skip)
      return {
        isLoading: loading,
        error: error || null,
        data: null,
        query,
      };

    if (loading)
      return {
        isLoading: loading,
        error: null,
        data: null,
        query,
      };

    if (error) {
      console.error(error);

      return {
        isLoading: loading,
        error: error || null,
        data: null,
        query,
      };
    }

    if (arrow)
      return {
        isLoading: loading,
        error: error || null,
        data: arrow.toArray().map((row) => row.toJSON()),
        query,
      };

    return {
      isLoading: loading,
      error: error || null,
      data: null,
      query,
    };
  }, [arrow, loading, error, skip, query]);

  return result;
}

export default useDuckQueryScanData;
